/**
 * Settings as a blurred modal shell: identity rail + integrations, preferences, and shortcuts.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from '../../ui/common/Modal';
import { Avatar } from '../../ui/common';
import { Button } from '../../ui/common/Button';
import { useAuth } from '../../auth';
import { APP_VERSION_INFO, formatFullBuildInfo } from '../../config/version';
import { SettingsView, type SettingsSectionId, type SettingsSourceId } from './SettingsView';
import {
  isConnectableProviderSection,
  OPEN_SETTINGS_EVENT,
  parseSettingsNavigation,
  parseSettingsSectionParam,
  providerOauthReturnMessage,
  type ProviderOauthReturn,
} from './settingsDeepLink';

export interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialSection?: SettingsSectionId;
}

const NAV: Array<{ id: SettingsSectionId; label: string }> = [
  { id: 'people', label: 'Personas' },
  { id: 'providers', label: 'Proveedores' },
  { id: 'ai', label: 'IA' },
  { id: 'alerts', label: 'Avisos' },
  { id: 'appearance', label: 'Apariencia' },
  { id: 'shortcuts', label: 'Atajos' },
  { id: 'changelog', label: 'Novedades' },
];

export const SettingsModal: React.FC<SettingsModalProps> = ({
  isOpen,
  onClose,
  initialSection = 'providers',
}) => {
  const { user, signOut } = useAuth();
  const [section, setSection] = useState<SettingsSectionId>(initialSection);
  const [source, setSource] = useState<SettingsSourceId | null>(null);
  const [oauthReturn, setOauthReturn] = useState<ProviderOauthReturn | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    const navigation = parseSettingsNavigation(window.location.search, initialSection);
    setSection(navigation.section);
    setSource(navigation.source);
    setOauthReturn(navigation.oauthReturn);
  }, [isOpen, initialSection]);

  // `openSettingsSection` used to be fired only from outside the modal, where
  // reopening re-ran the effect above. A link inside the modal (Settings › IA
  // pointing at Proveedores or Slack › Voz) changes no dependency, so without
  // this listener the click did nothing at all.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onOpenSettings = (event: Event) => {
      const detail = (event as CustomEvent<{ section?: SettingsSectionId }>).detail;
      const next = parseSettingsSectionParam(detail?.section);
      if (!next) return;
      setSection(isConnectableProviderSection(next) ? 'providers' : next);
      setSource(isConnectableProviderSection(next) ? (next as SettingsSourceId) : null);
      // Same as the nav buttons: a previous OAuth result does not belong to
      // the section the user just navigated to.
      setOauthReturn(null);
    };
    window.addEventListener(OPEN_SETTINGS_EVENT, onOpenSettings);
    return () => window.removeEventListener(OPEN_SETTINGS_EVENT, onOpenSettings);
  }, [isOpen]);

  const syncProviderParam = (next: SettingsSourceId | null) => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    url.searchParams.set('settings', 'providers');
    if (next) url.searchParams.set('provider', next);
    else url.searchParams.delete('provider');
    url.searchParams.delete('oauth');
    url.searchParams.delete('oauth_reason');
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
  };

  const displayName = user?.displayName || user?.email || 'Signed in';
  const initials = useMemo(() => {
    const source = user?.displayName || user?.email || 'ZU';
    return source.slice(0, 2).toUpperCase();
  }, [user?.displayName, user?.email]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      width={960}
      ariaLabel="Configuración"
      title="Configuración"
      description="Integraciones, preferencias y atajos de teclado"
      showCloseButton
      fullscreenOnMobile
    >
      <div data-testid="settings-modal" className="z-settings-grid">
        <aside data-testid="settings-identity-rail" className="z-settings-identity-rail">
          <div
            className="z-settings-profile-header"
            style={{ display: 'flex', alignItems: 'center', gap: 12 }}
          >
            <Avatar
              src={user?.photoURL ?? undefined}
              name={displayName}
              initials={initials}
              color="var(--z-accent)"
              size={44}
            />
            <div style={{ minWidth: 0, flex: '1 1 auto' }}>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: 'var(--z-text)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {displayName}
              </div>
              {user?.email && (
                <div
                  style={{
                    fontSize: 11.5,
                    color: 'var(--z-dim)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {user.email}
                </div>
              )}
            </div>
          </div>

          <div className="z-modal-sticky-tabs z-settings-sticky-nav">
            <nav
              className="z-segmented-tabs z-settings-nav-list"
              aria-label="Secciones de configuración"
            >
              {NAV.map((item) => {
                const active = section === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    data-testid={`settings-nav-${item.id}`}
                    onClick={() => {
                      setSection(item.id);
                      setSource(null);
                      setOauthReturn(null);
                      if (typeof window !== 'undefined') {
                        const url = new URL(window.location.href);
                        url.searchParams.set('settings', item.id);
                        url.searchParams.delete('provider');
                        url.searchParams.delete('oauth');
                        url.searchParams.delete('oauth_reason');
                        url.searchParams.delete('jira');
                        window.history.replaceState(
                          {},
                          '',
                          `${url.pathname}${url.search}${url.hash}`,
                        );
                      }
                    }}
                    className={`z-segmented-tab-btn ${active ? 'z-segmented-tab-btn--active' : ''}`}
                  >
                    {item.label}
                  </button>
                );
              })}
            </nav>
          </div>

          <div
            className="z-settings-identity-footer"
            style={{
              marginTop: 'auto',
              paddingTop: 12,
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            <Button
              size="sm"
              variant="ghost"
              data-testid="settings-sign-out"
              onClick={() => void signOut()}
            >
              Cerrar sesión
            </Button>
            <div
              data-testid="settings-version-info"
              title={formatFullBuildInfo()}
              style={{
                fontSize: 11,
                color: 'var(--z-dim)',
                lineHeight: 1.4,
                padding: '0 4px',
                userSelect: 'none',
              }}
            >
              <div>
                Zunelo {APP_VERSION_INFO.formatted} ({APP_VERSION_INFO.commitHash})
              </div>
              <div>Build: {APP_VERSION_INFO.buildTime.split('T')[0]}</div>
              <a
                href={APP_VERSION_INFO.releaseUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="settings-version-release-link"
                style={{ color: 'inherit', textDecoration: 'underline' }}
              >
                GitHub {APP_VERSION_INFO.formatted}
              </a>
            </div>
          </div>
        </aside>

        <div className="z-settings-content-pane" style={{ minWidth: 0 }}>
          {oauthReturn ? (
            <div
              data-testid="settings-provider-oauth-return"
              role="status"
              style={{
                marginBottom: 12,
                padding: '10px 12px',
                borderRadius: 8,
                background:
                  oauthReturn.status === 'connected' ? 'var(--z-ok-soft)' : 'var(--z-bad-soft)',
                color: oauthReturn.status === 'connected' ? 'var(--z-ok)' : 'var(--z-danger)',
                fontSize: 13,
              }}
            >
              {providerOauthReturnMessage(oauthReturn)}
            </div>
          ) : null}
          <SettingsView
            section={section}
            source={source}
            onSourceChange={(next) => {
              setSource(next);
              setOauthReturn(null);
              syncProviderParam(next);
            }}
            embedded
            userId={user?.uid}
          />
        </div>
      </div>
    </Modal>
  );
};
